import { useState, useEffect } from "react";
import { admin } from "@/lib/api";
import Spinner from "@/components/Spinner";
import { Save, RotateCcw, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import clsx from "clsx";

interface Prompt {
  prompt_key: string;
  label: string;
  category: string;
  description: string;
  system_prompt: string;
  is_custom: boolean;
  updated_at: string | null;
}

const CATEGORY_LABELS: Record<string, string> = {
  campaigns: "Campaigns",
  deliverability: "Deliverability",
  sequences: "Sequences",
  warmup: "Warmup",
  intelligence: "Intelligence",
};

const CATEGORY_ORDER = ["campaigns", "deliverability", "sequences", "warmup", "intelligence"];

export default function AdminPromptsPage() {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "ok" | "err" } | null>(null);

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }
  }, [toast]);

  async function load() {
    try {
      const data = await admin.prompts.list();
      setPrompts(data.prompts);
    } catch {
      setToast({ msg: "Failed to load prompts", type: "err" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // Group by category
  const grouped = CATEGORY_ORDER
    .map((cat) => ({
      category: cat,
      label: CATEGORY_LABELS[cat] || cat,
      items: prompts.filter((p) => p.category === cat),
    }))
    .filter((g) => g.items.length > 0);

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-ink">AI Prompts</h1>
        <p className="text-sm text-slate mt-1">Edit the system prompts used by all AI features. Changes take effect within 60 seconds.</p>
      </div>

      {grouped.map((group) => (
        <div key={group.category} className="space-y-3">
          <h2 className="text-xs font-bold text-slate uppercase tracking-wider">{group.label}</h2>
          {group.items.map((p) => (
            <div key={p.prompt_key} className="bg-white rounded-2xl shadow-card overflow-hidden">
              <div
                className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-black/[0.015] transition"
                onClick={() => setExpandedKey(expandedKey === p.prompt_key ? null : p.prompt_key)}
              >
                <Sparkles size={16} className={p.is_custom ? "text-amber-500" : "text-slate/40"} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-ink">{p.label}</span>
                    <span className="text-[10px] font-mono text-slate bg-black/[0.04] px-1.5 py-0.5 rounded">{p.prompt_key}</span>
                    {p.is_custom && <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Customised</span>}
                  </div>
                  <p className="text-xs text-slate mt-0.5">{p.description}</p>
                </div>
                {expandedKey === p.prompt_key ? <ChevronUp size={16} className="text-slate" /> : <ChevronDown size={16} className="text-slate" />}
              </div>

              {expandedKey === p.prompt_key && (
                <PromptEditor
                  prompt={p}
                  onSaved={(updated) => {
                    setPrompts((prev) => prev.map((x) => (x.prompt_key === updated.prompt_key ? updated : x)));
                    setToast({ msg: `Saved "${updated.label}"`, type: "ok" });
                  }}
                  onReset={(reset) => {
                    setPrompts((prev) => prev.map((x) => (x.prompt_key === reset.prompt_key ? reset : x)));
                    setToast({ msg: `Reset "${reset.label}" to default`, type: "ok" });
                  }}
                  onError={(msg) => setToast({ msg, type: "err" })}
                />
              )}
            </div>
          ))}
        </div>
      ))}

      {toast && (
        <div className={clsx(
          "fixed bottom-6 right-6 px-4 py-2.5 rounded-xl shadow-lg text-sm font-semibold z-50 animate-in slide-in-from-bottom-2",
          toast.type === "ok" ? "bg-teal text-white" : "bg-red-500 text-white"
        )}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function PromptEditor({
  prompt: p,
  onSaved,
  onReset,
  onError,
}: {
  prompt: Prompt;
  onSaved: (p: Prompt) => void;
  onReset: (p: Prompt) => void;
  onError: (msg: string) => void;
}) {
  const [draft, setDraft] = useState(p.system_prompt);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const dirty = draft !== p.system_prompt;

  async function handleSave() {
    setSaving(true);
    try {
      await admin.prompts.update(p.prompt_key, draft);
      onSaved({ ...p, system_prompt: draft, is_custom: true, updated_at: new Date().toISOString() });
    } catch {
      onError(`Failed to save "${p.label}"`);
    }
    setSaving(false);
  }

  async function handleReset() {
    if (!confirm("Reset this prompt to the hardcoded default? Your customisation will be lost.")) return;
    setResetting(true);
    try {
      const data = await admin.prompts.reset(p.prompt_key);
      setDraft(data.system_prompt);
      onReset({ ...p, system_prompt: data.system_prompt, is_custom: false, updated_at: null });
    } catch {
      onError(`Failed to reset "${p.label}"`);
    }
    setResetting(false);
  }

  async function handleShowDefault() {
    try {
      const data = await admin.prompts.getDefault(p.prompt_key);
      setDraft(data.system_prompt);
    } catch {
      onError("Failed to load default prompt");
    }
  }

  return (
    <div className="border-t border-black/[0.04] px-5 py-5 space-y-4">
      <div>
        <label className="block text-[10px] font-bold text-slate uppercase tracking-wider mb-1">System Prompt</label>
        <textarea
          className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm font-mono min-h-[250px] leading-relaxed"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <p className="text-[10px] text-slate mt-1">{draft.length.toLocaleString()} characters</p>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className={clsx(
            "flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl transition",
            dirty ? "bg-teal text-white hover:opacity-90" : "bg-black/[0.04] text-slate cursor-not-allowed"
          )}
        >
          {saving ? <Spinner /> : <Save size={14} />}
          {dirty ? "Save Changes" : "No Changes"}
        </button>

        {p.is_custom && (
          <button
            onClick={handleReset}
            disabled={resetting}
            className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl border border-black/10 text-slate hover:text-red-500 hover:border-red-200 transition"
          >
            {resetting ? <Spinner /> : <RotateCcw size={14} />}
            Reset to Default
          </button>
        )}

        {p.is_custom && (
          <button
            onClick={handleShowDefault}
            className="text-xs text-slate hover:text-ink underline transition"
          >
            View default
          </button>
        )}
      </div>

      {p.updated_at && (
        <p className="text-[10px] text-slate">Last updated: {new Date(p.updated_at).toLocaleString()}</p>
      )}
    </div>
  );
}
